import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useAuthStore } from "../../store/useStore";
import PageHeader from "../../components/common/PageHeader";

const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
`;

const FormSection = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  color: #333;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 600;
  padding-bottom: 10px;
  border-bottom: 2px solid #f0f0f0;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 8px;
  color: #555;
  font-weight: 500;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #d2691e;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const InfoText = styled.div`
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 20px;
  border-left: 4px solid #d2691e;
`;

const CurrentInfo = styled.div`
  background: #fef3e7;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const CurrentInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CurrentInfoLabel = styled.span`
  font-weight: 500;
  color: #d2691e;
`;

const CurrentInfoValue = styled.span`
  color: #333;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SaveButton = styled(Button)`
  background: linear-gradient(135deg, #d2691e 0%, #b85a1a 100%);
  color: white;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(210, 105, 30, 0.4);
  }
`;

const CancelButton = styled(Button)`
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;

  &:hover {
    background: #e9ecef;
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  border: 1px solid #c3e6cb;
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #d00;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  border: 1px solid #fcc;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

function Profile() {
  const { admin, updateAdminInfo, isLoading, error, clearError } =
    useAuthStore();

  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
  });

  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (admin) {
      setFormData({
        email: admin.email || "",
        name: admin.name || "",
        password: "",
      });
    }
  }, [admin]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // 메시지 초기화
    if (error) {
      clearError();
    }
    if (successMessage) {
      setSuccessMessage("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 변경된 필드만 전송
    const updateData = {};
    if (formData.email !== admin.email) {
      updateData.email = formData.email;
    }
    if (formData.name !== admin.name) {
      updateData.name = formData.name;
    }
    if (formData.password) {
      updateData.password = formData.password;
    }

    // 변경사항이 없다면
    if (Object.keys(updateData).length === 0) {
      setSuccessMessage("변경사항이 없습니다.");
      return;
    }

    const result = await updateAdminInfo(updateData);

    if (result.success) {
      setSuccessMessage("정보가 성공적으로 업데이트되었습니다.");
      // 비밀번호 필드 초기화
      setFormData((prev) => ({ ...prev, password: "" }));
    }
  };

  const handleCancel = () => {
    if (admin) {
      setFormData({
        email: admin.email || "",
        name: admin.name || "",
        password: "",
      });
    }
    clearError();
    setSuccessMessage("");
  };

  if (!admin) {
    return null;
  }

  return (
    <ProfileContainer>
      <PageHeader
        title="관리자 프로필"
        subtitle="관리자 계정 정보를 수정할 수 있습니다"
      />

      <ProfileCard>
        <FormSection>
          <SectionTitle>현재 정보</SectionTitle>
          <CurrentInfo>
            <CurrentInfoRow>
              <CurrentInfoLabel>이메일:</CurrentInfoLabel>
              <CurrentInfoValue>{admin.email}</CurrentInfoValue>
            </CurrentInfoRow>
            <CurrentInfoRow>
              <CurrentInfoLabel>이름:</CurrentInfoLabel>
              <CurrentInfoValue>{admin.name}</CurrentInfoValue>
            </CurrentInfoRow>
            <CurrentInfoRow>
              <CurrentInfoLabel>역할:</CurrentInfoLabel>
              <CurrentInfoValue>{admin.role}</CurrentInfoValue>
            </CurrentInfoRow>
            <CurrentInfoRow>
              <CurrentInfoLabel>관리자 ID:</CurrentInfoLabel>
              <CurrentInfoValue>{admin.id}</CurrentInfoValue>
            </CurrentInfoRow>
          </CurrentInfo>
        </FormSection>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>정보 수정</SectionTitle>

            <InfoText>
              변경하고 싶은 정보만 입력하세요. 빈 필드는 변경되지 않습니다.
            </InfoText>

            {successMessage && (
              <SuccessMessage>{successMessage}</SuccessMessage>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <FormRow>
              <FormGroup>
                <Label>새 이메일</Label>
                <Input
                  type="email"
                  name="email"
                  placeholder="새 이메일을 입력하세요"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>새 이름</Label>
                <Input
                  type="text"
                  name="name"
                  placeholder="새 이름을 입력하세요"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>새 비밀번호</Label>
              <Input
                type="password"
                name="password"
                placeholder="새 비밀번호를 입력하세요 (변경하지 않으려면 비워두세요)"
                value={formData.password}
                onChange={handleInputChange}
              />
            </FormGroup>
          </FormSection>

          <ButtonGroup>
            <CancelButton type="button" onClick={handleCancel}>
              취소
            </CancelButton>
            <SaveButton type="submit" disabled={isLoading}>
              {isLoading && <LoadingSpinner />}
              {isLoading ? "저장 중..." : "저장"}
            </SaveButton>
          </ButtonGroup>
        </form>
      </ProfileCard>
    </ProfileContainer>
  );
}

export default Profile;
